import { useEffect, useState } from "react";
import { EsqueletoDashboard } from "../../components/Esqueleto";
import { EstadoVacio } from "../../components/EstadoVacio";
import { GraficoBarras } from "../../components/GraficoBarras";
import { api } from "../../lib/apiClient";
import { PanelObjetivos } from "./PanelObjetivos";
import { SeccionDashboard, TarjetaGrafico, TarjetaKPI, formatearPorMoneda } from "./ComunesDashboard";
import type { FilaMoneda } from "./ComunesDashboard";

interface DatosEjecutivo {
  finanzas: {
    facturacionMes: FilaMoneda[];
    honorariosCobradosMes: FilaMoneda[];
    gastosMes: FilaMoneda[];
    resultadoMes: FilaMoneda[];
    flujoDeCaja: { mes: string; moneda: string; ingresos: number; gastos: number }[];
    saldoCajas: { id: string; nombre: string; moneda: string; saldo: number }[];
    // Sólo vienen cuando el Header tiene una Unidad puntual seleccionada
    // (?area_practica_id=) — ver routes/dashboard.ts. En "Ver: Todas" no
    // tienen sentido (repartir un gasto general entre "todo" es circular).
    gastosGeneralesAsignados?: FilaMoneda[];
    rentabilidad?: FilaMoneda[];
  } | null;
  clientes: {
    activos: number;
    nuevosEsteMes: number;
    expedientesAbiertos: number;
    expedientesCerrados: number;
  };
  rrhh: {
    productividadPorAbogado: { nombre: string; total: number }[] | null;
  };
  objetivos: {
    definidos: boolean;
    honorariosCobrados: { objetivo: number; moneda: string; actual: number } | null;
    escritos: { objetivo: number; actual: number } | null;
    audiencias: { objetivo: number; actual: number } | null;
    nuevosClientes: { objetivo: number; actual: number } | null;
  };
}

// Vista de profundización "Financiero" (spec Dashboard): administra el
// estudio como empresa — finanzas, clientes, RRHH, objetivos. Sin
// información procesal (eso vive en la vista Judicial).
//
// Lo que NO vive acá (auditoría UX, aplicado sin volver a discutirlo):
// Resultado anual, Gastos por Categoría, el detalle de Presupuestos de
// Honorarios y el Comparador de Unidades completo — son detalle que ya
// vive en el módulo Economía (filtro por Categoría/Unidad en la tabla de
// movimientos) o en Presupuestador de Honorarios; mostrarlos acá también
// sería duplicar el mismo nivel de detalle en dos lugares.
// "Casos abiertos/cerrados" (RRHH) tampoco: es exactamente el mismo dato
// que "Expedientes abiertos/cerrados" (Clientes, más abajo) — un único
// campo unificado en el backend (ver routes/dashboard.ts), un único lugar
// acá.
export function DashboardEjecutivoPage() {
  const [datos, setDatos] = useState<DatosEjecutivo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<DatosEjecutivo>("/dashboard/ejecutivo")
      .then(setDatos)
      .catch(() => setError("No se pudo cargar el panel."));
  }, []);

  if (error) return <EstadoVacio titulo="Error" descripcion={error} />;
  if (!datos) return <EsqueletoDashboard />;

  const f = datos.finanzas;

  return (
    <div>
      {f && (
        <SeccionDashboard titulo="Finanzas">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <TarjetaKPI
              etiqueta="Facturación del mes"
              valor={formatearPorMoneda(f.facturacionMes)}
              icono="economia"
              tono="finanzas"
              destacado
              className="sm:col-span-2"
            />
            <TarjetaKPI
              etiqueta="Honorarios cobrados"
              valor={formatearPorMoneda(f.honorariosCobradosMes)}
              icono="economia"
              tono="finanzas"
            />
            <TarjetaKPI etiqueta="Gastos del mes" valor={formatearPorMoneda(f.gastosMes)} icono="economia" tono="finanzas" />
            <TarjetaKPI etiqueta="Resultado del mes" valor={formatearPorMoneda(f.resultadoMes)} icono="economia" tono="finanzas" />
            {f.gastosGeneralesAsignados && (
              <TarjetaKPI
                etiqueta="Gastos generales asignados"
                valor={formatearPorMoneda(f.gastosGeneralesAsignados)}
                icono="economia"
                tono="finanzas"
              />
            )}
            {f.rentabilidad && (
              <TarjetaKPI
                etiqueta="Rentabilidad"
                valor={f.rentabilidad.map((r) => `${(r.total * 100).toFixed(1)}% ${r.moneda}`).join(" · ") || "—"}
                icono="economia"
                tono="finanzas"
              />
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TarjetaGrafico titulo="Saldo de Cajas" icono="economia">
              {f.saldoCajas.length === 0 ? (
                <EstadoVacio titulo="Sin Cajas activas" />
              ) : (
                <GraficoBarras
                  datos={f.saldoCajas.map((c) => ({ etiqueta: `${c.nombre} (${c.moneda})`, valor: c.saldo }))}
                />
              )}
            </TarjetaGrafico>

            <TarjetaGrafico titulo="Flujo de Caja (últimos 6 meses)" icono="economia">
              {f.flujoDeCaja.length === 0 ? (
                <EstadoVacio titulo="Sin movimientos en el período" />
              ) : (
                <ul className="divide-y divide-borde">
                  {f.flujoDeCaja.map((m, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 py-3.5 text-sm">
                      <span className="font-medium text-texto">{m.mes} ({m.moneda})</span>
                      <span className="flex-shrink-0 whitespace-nowrap text-texto-secundario">
                        <span className="text-exito">Ingresos {m.ingresos.toLocaleString("es-AR")}</span>
                        {" · "}
                        <span className="text-error">Gastos {m.gastos.toLocaleString("es-AR")}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </TarjetaGrafico>
          </div>
        </SeccionDashboard>
      )}

      <SeccionDashboard titulo="Clientes">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <TarjetaKPI etiqueta="Clientes activos" valor={datos.clientes.activos} icono="clientes" tono="info" />
          <TarjetaKPI etiqueta="Nuevos este mes" valor={datos.clientes.nuevosEsteMes} icono="clientes" tono="exito" />
          <TarjetaKPI etiqueta="Expedientes abiertos" valor={datos.clientes.expedientesAbiertos} icono="expedientes" tono="info" />
          <TarjetaKPI etiqueta="Expedientes cerrados" valor={datos.clientes.expedientesCerrados} icono="expedientes" tono="neutro" />
        </div>
      </SeccionDashboard>

      {datos.rrhh.productividadPorAbogado && (
        <SeccionDashboard titulo="RRHH">
          <TarjetaGrafico titulo="Productividad por abogado (mes)" icono="usuarios">
            {datos.rrhh.productividadPorAbogado.length === 0 ? (
              <EstadoVacio titulo="Sin actuaciones este mes" />
            ) : (
              <GraficoBarras
                datos={datos.rrhh.productividadPorAbogado.map((p) => ({ etiqueta: p.nombre, valor: p.total }))}
              />
            )}
          </TarjetaGrafico>
        </SeccionDashboard>
      )}

      <SeccionDashboard titulo="Objetivos">
        <PanelObjetivos objetivos={datos.objetivos} />
      </SeccionDashboard>
    </div>
  );
}
